<template>
    <div class="tab-content">
        <medical-form
            v-if="activeTab === 'appointments'"
            :appointments="appointments"
            :isLoadingAppointments="isLoadingAppointments"
            :appointmentError="appointmentError"
            :hasMoreAppointments="hasMoreAppointments"
            @load-more="loadMoreAppointments"
        ></medical-form>
        <details-form v-if="activeTab === 'documents'"></details-form>
        <profile-form
            v-if="activeTab === 'profile'"
            :patientDetails="patientDetails"
            @update-patientDetails="onPatientUpdate"
        ></profile-form>
        <request-form
            v-if="activeTab === 'request-records'"
            :availHipaaForms="availHipaaForms"
            @refresh-hipaa="getPatientHipaaForms"
        ></request-form>

        <record-requests-inbox v-if="activeTab === 'record-requests-inbox'"></record-requests-inbox>

        <hippa-form
            v-if="activeTab === 'hipaa-forms'"
            :availHipaaForms="availHipaaForms"
            @refresh-hipaa="getPatientHipaaForms"
        ></hippa-form>

        <general-intake-wizard
            v-if="activeTab === 'general-intake' || activeTab === 'intake-forms'"
            :patientDetails="patientDetails"
            :patientId="computedPatientId"
            :authenticated="true"
            @close="$emit('toggle', 'profile')"
        ></general-intake-wizard>

        <accident-intake-wizard
            v-if="activeTab === 'accident-intake'"
            :patientDetails="patientDetails"
            :patientId="computedPatientId"
            :authenticated="true"
            @close="$emit('toggle', 'profile')"
        ></accident-intake-wizard>

        <refer-patient-form
            v-if="activeTab === 'refer-patient'"
            :patientDetails="patientDetails"
            @open-direct-referral="openDirectReferral"
        ></refer-patient-form>

        <patient-chatbox v-if="activeTab === 'message-patient'" :patientDetails="patientDetails"></patient-chatbox>

        <delete-patient-modal
            v-if="activeTab === 'delete-patient'"
            :isOpen="true"
            @close="closeDeletePatientModal"
        ></delete-patient-modal>
    </div>
</template>
<script>
    import MedicalForm from 'components/forms/medical/MedicalForm'
    import DetailsForm from 'components/forms/all-details/DetailsForm'
    import ProfileForm from 'components/forms/patient-profile/ProfileForm'
    import RequestForm from 'components/forms/request-records/RequestForm'
    import ReferPatientForm from 'components/forms/patient/ReferPatientForm'
    import HippaForm from 'components/forms/hippa/HippaForm'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PatientRecordsAPI from 'api/PatientRecordsAPI'
    import DeletePatientModal from 'components/page-elements/patient/DeletePatientModal'
    import RecordRequestsInbox from 'components/forms/record-requests-inbox/RecordRequestsInbox'
    import PatientChatbox from 'components/forms/patient/PatientChatbox'
    import AccidentIntakeWizard from 'components/forms/AccidentIntakeWizard'
    import GeneralIntakeWizard from 'components/forms/GeneralIntakeWizard'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'

    export default {
        components: {
            MedicalForm,
            DetailsForm,
            ProfileForm,
            RequestForm,
            ReferPatientForm,
            HippaForm,
            DeletePatientModal,
            RecordRequestsInbox,
            PatientChatbox,
            AccidentIntakeWizard,
            GeneralIntakeWizard,
        },
        props: {
            activeTab: {
                type: String,
                default: 'profile',
            },
            patientDetails: {
                type: Object,
                default: () => ({}),
            },
            patientId: {
                type: [String, Number],
                default: null,
            },
        },

        data() {
            return {
                availHipaaForms: [],
                appointments: [],
                appointmentsLimit: 10,
                appointmentsOffset: 0,
                appointmentsTotal: 0,
                isLoadingAppointments: false,
                appointmentError: '',
            }
        },
        computed: {
            computedPatientId() {
                return this.patientId || this.$route.params.id
            },
            hasMoreAppointments() {
                return this.appointments.length < this.appointmentsTotal
            },
        },
        methods: {
            onPatientUpdate() {
                this.emitEvent({ name: 'update-patient' })
            },
            getPatientHipaaForms() {
                if (!this.computedPatientId) return
                PatientRecordsAPI.getHipaaFormList(this.$auth.getActiveMember()?.id, this.computedPatientId).then((res) => {
                    this.availHipaaForms = res
                })
            },
            closeDeletePatientModal() {
                this.emitEvent({ name: 'close-delete-patient' })
            },
            openDirectReferral(practiceType) {
                this.emitEvent({ name: 'open-direct-referral', payload: practiceType })
            },
            fetchAppointments({ append = false } = {}) {
                if (!this.computedPatientId || this.isLoadingAppointments) return

                this.isLoadingAppointments = true
                this.appointmentError = ''
                const offset = append ? this.appointmentsOffset + this.appointmentsLimit : 0
                const userId = this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                const practiceId = this.$auth.getPractice()?.id
                ReferralsAPI.getListV2({
                    patientId: this.computedPatientId,
                    limit: this.appointmentsLimit,
                    type: ReferralPathTypeEnum.RECEIVED,
                    offset,
                    userId,
                    practiceId,
                })
                    .then((res) => {
                        const appointments = Array.isArray(res) ? res : []
                        this.appointments = append ? this.appointments.concat(appointments) : appointments
                        this.appointmentsOffset = offset
                        this.appointmentsTotal = Number(res._headers?.['x-total-count'] || this.appointments.length)
                    })
                    .catch(() => {
                        this.appointmentError = 'Unable to load appointments.'
                    })
                    .finally(() => {
                        this.isLoadingAppointments = false
                    })
            },
            loadMoreAppointments() {
                if (this.isLoadingAppointments || !this.hasMoreAppointments) return
                this.fetchAppointments({ append: true })
            },
        },
        watch: {
            activeTab: {
                immediate: true,
                handler(newTab) {
                    if (newTab === 'appointments' && !this.appointments.length) {
                        this.fetchAppointments()
                    }
                    if ((newTab === 'hipaa-forms' || newTab === 'request-records') && !this.availHipaaForms.length) {
                        this.getPatientHipaaForms()
                    }
                },
            },
        },
    }
</script>
